import { Component, Prop, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { SharedVehicleService, VehicleItem, VehicleService } from '@/services';
import { FormControlClass, VuetifySelectItem } from '@/core';
import { FindAllOptions, ScheduleFilter } from '@/services/sharedVehicle/interface';
import DocumentsDialog from '@/pages/TripsPage/components/TripDialog/components/DocumentsDialog/DocumentsDialog.vue';

const vehicleStore = namespace('Vehicle');

@Component({
  components: { DocumentsDialog },
})
export default class VehicleSelect extends FormControlClass<string> {
  @vehicleStore.Mutation
  protected readonly setVehicles!: (vehicles: VehicleItem[]) => void;

  @Prop({ default: false })
  private readonly for2pl!: boolean;

  @Prop({ default: false })
  private readonly isFleetOwner!: boolean;

  @Prop()
  private readonly partnerId!: string | undefined;

  @Prop({ default: false })
  private readonly isPartnerRequired!: boolean;

  @Prop()
  private readonly scheduleFilter: ScheduleFilter | undefined;

  @Prop({ default: false })
  private readonly showType!: boolean;

  @Prop({ default: false })
  private readonly showExpireService!: boolean;

  @Prop({ default: () => [] })
  private readonly excludeVehiclesIds!: string[];

  @Prop({ default: false })
  private readonly checkOnline!: boolean;

  @Prop()
  private readonly totalCargoWeight: number | undefined;

  @Prop()
  private readonly totalCargoSpace: number | undefined;

  private defaultValue!: VuetifySelectItem<string>[];

  private isSelectedAll = false;

  public page = 1;

  public search = '';

  public items: VuetifySelectItem<string>[] = [];

  public mounted() {
    setTimeout(async () => {
      await this.setData();
    }, 4000);
  }

  public async handleSearchUpdate(val:string) {
    if (val?.length >= 3) {
      await this.setData(val);
    }
  }

  public async setData(searchQuery?: string): Promise<void> {
    if (this.isPartnerRequired && !this.partnerId) return;
    if (!this.totalCargoWeight || !this.totalCargoSpace) return;

    try {
      this.isLoading = true;

      let vehicles: VehicleItem[];
      if (this.items.length === 0 && !this.items?.some(i => i.value === this.value)) {
        try {
          const vehicleItem = await VehicleService.findOne(this.value);
          let text = this.showType ? `${vehicleItem.registrationPlate} / ${vehicleItem.type.name}` : vehicleItem.registrationPlate;
          if (this.showExpireService) {
            text += ` ${vehicleItem.serviceIntervalInfo.isExpired ? `/ ${vehicleItem.serviceIntervalInfo.text}` : ''}`;
          }
          this.items = [{ value: vehicleItem.id, text: text.trim() }];
        } catch (error) {
          console.error('Failed to fetch vehicleItem', error);
        }
      }
      if (this.isFleetOwner) {
        if (searchQuery && searchQuery?.length >= 3) {
          const { data } = await VehicleService.findAll({
            page: 1,
            limit: 10,
            search: searchQuery,
            weight: this.totalCargoWeight,
            cargoSpace: this.totalCargoSpace,
          });
          vehicles = data;
        } else {
          const { data } = await VehicleService.findAll({
            page: this.page,
            limit: 10,
            weight: this.totalCargoWeight,
            cargoSpace: this.totalCargoSpace,
          });
          vehicles = data;
        }
      } else {
        let options: FindAllOptions;
        if (searchQuery && searchQuery?.length >= 3) {
          options = {
            page: 1,
            limit: 10,
            search: searchQuery,
            weight: this.totalCargoWeight,
            cargoSpace: this.totalCargoSpace,
          };
        } else {
          options = {
            page: this.page,
            limit: 10,
            search: searchQuery,
            weight: this.totalCargoWeight,
            cargoSpace: this.totalCargoSpace,
          };
        }

        if (!this.for2pl && this.partnerId) {
          options.partnerId = this.partnerId;
        }

        if (this.scheduleFilter) {
          options.scheduleFilter = this.scheduleFilter;
        }

        if (this.for2pl && this.partnerId) {
          options.partnerId = this.partnerId;
          options.isPl2 = true;
        }
        const { data } = await SharedVehicleService.findAllAvailable(options, true);

        if (this.customFilter !== undefined) {
          vehicles = data.filter(this.customFilter).map(v => v.vehicle);
        }
        vehicles = data.map(v => v.vehicle);
      }
      this.setVehicles([...vehicles]);

      const vehicleWithType = vehicles.map((v) => {
        let text = this.showType ? `${v.registrationPlate} / ${v.type.name}` : v.registrationPlate;
        if (this.showExpireService) {
          text += ` ${v.serviceIntervalInfo.isExpired ? `/ ${v.serviceIntervalInfo.text}` : ''}`;
        }
        return { value: v.id, text: text.trim() };
      });
      const existingIds = new Set(this.items.map(item => item.value));
      const newItems = vehicleWithType.filter(item => !existingIds.has(item.value));
      this.items = [...this.items, ...newItems];
    } finally {
      this.isLoading = false;
    }
  }

  public async loadMore(entries: any) {
    if (entries[0].isIntersecting) {
      this.page += 1;
      await this.setData();
    }
  }

  public selectAll(): void {
    this.isSelectedAll = !this.isSelectedAll;
    if (this.isSelectedAll) {
      this.$emit('input', this.items.map((i) => (i as VuetifySelectItem<string>).value));
    } else {
      this.$emit('input', []);
    }
  }

  @Watch('value', { immediate: true })
  private async vehicleSelected(): Promise<void> {
    if (this.checkOnline && this.value) {
      const isOnline = await VehicleService.isOnline(this.value);
      if (!isOnline) {
        (this.items as VuetifySelectItem<string>[]) = (this.items as VuetifySelectItem<string>[]).map(i => {
          if (i.value === this.value) {
            return { ...i, text: !i.text.includes('GPS Fault') ? `${i.text} / GPS Fault` : i.text };
          }

          return i;
        });
      }
    }
  }

  @Watch('excludeVehiclesIds')
  private excludeVehiclesIdsChanged(): void {
    this.items = (this.items as VuetifySelectItem<string>[]).filter(i => !this.excludeVehiclesIds.includes(i.value));
  }
}
